import { useMemo, useState, useEffect, createContext, lazy, Suspense } from 'react';
import { getRouteRequirement } from './lib/routePermissions';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createClinicTheme } from './theme/clinicTheme';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { toast } from 'react-hot-toast';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { AppLockProvider } from './contexts/AppLockContext';
import { PatientDetailsProvider } from './contexts/PatientDetailsContext';
// CHATBOT - Commented out as not using chatbot feature
// import { ChatProvider } from './contexts/ChatContext';
import { PatientAuthProvider, usePatientAuth } from './contexts/PatientAuthContext';
import { PatientTenantProvider } from './contexts/PatientTenantContext';
import { PatientDependentProvider } from './contexts/PatientDependentContext';
import { LockScreen } from './components/security/LockScreen';
import { TourProvider } from './contexts/TourContext';
import { Login } from './components/auth/Login';
const PharmacyLogin = lazy(() => import('./components/auth/PharmacyLogin').then(m => ({ default: m.PharmacyLogin })));
import { SessionTimeoutWarning } from './components/auth/SessionTimeoutWarning';
import { Layout } from './components/layout/Layout';
import { PatientPortalLayout } from './components/patient-portal/PatientPortalLayout';
import { PatientLogin } from './components/patient-portal/PatientLogin';
import { Box, CircularProgress } from '@mui/material';
import { UserRole } from './types';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Lazy-loaded route components — split into separate chunks, loaded on demand
const ForcedPasswordReset = lazy(() => import('./components/auth/ForcedPasswordReset').then(m => ({ default: m.ForcedPasswordReset })));
const AcceptInvitation = lazy(() => import('./components/auth/AcceptInvitation').then(m => ({ default: m.AcceptInvitation })));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword').then(m => ({ default: m.ResetPassword })));
const ClinicSetup = lazy(() => import('./components/setup/ClinicSetup').then(m => ({ default: m.ClinicSetup })));
const TokenList = lazy(() => import('./components/tokens/TokenList').then(m => ({ default: m.TokenList })));
const PatientSearch = lazy(() => import('./components/patients/PatientSearch').then(m => ({ default: m.PatientSearch })));
const PatientDetailsPage = lazy(() => import('./pages/patients/PatientDetailsPage').then(m => ({ default: m.PatientDetailsPage })));
const BillingForm = lazy(() => import('./components/billing/BillingForm').then(m => ({ default: m.BillingForm })));
const BillingQueueScreen = lazy(() => import('./components/billing/BillingQueueScreen').then(m => ({ default: m.BillingQueueScreen })));
const FollowUpQueuePage = lazy(() => import('./pages/staff/FollowUpQueuePage').then(m => ({ default: m.FollowUpQueuePage })));
const DailyReport = lazy(() => import('./components/reports/DailyReport').then(m => ({ default: m.DailyReport })));
const VitalsExport = lazy(() => import('./components/reports/VitalsExport').then(m => ({ default: m.VitalsExport })));
const SuperAdminDashboard = lazy(() => import('./components/admin/SuperAdminDashboard'));
const SystemErrorLogs = lazy(() => import('./components/admin/SystemErrorLogs'));
const LabMetricManagement = lazy(() => import('./components/admin/LabMetricManagement'));
const LabManagement = lazy(() => import('./components/admin/LabManagement'));
const ClinicDashboard = lazy(() => import('./components/admin/ClinicDashboard'));
const InHousePharmacyManagement = lazy(() => import('./components/admin/InHousePharmacyManagement'));
const DoctorDashboard = lazy(() => import('./components/doctor/DoctorDashboard').then(m => ({ default: m.DoctorDashboard })));
const DoctorsSettings = lazy(() => import('./components/settings/DoctorsSettings').then(m => ({ default: m.DoctorsSettings })));
const DepartmentsSettings = lazy(() => import('./components/settings/DepartmentsSettings').then(m => ({ default: m.DepartmentsSettings })));
const PatientRegistrationForm = lazy(() => import('./components/patients/PatientRegistration').then(m => ({ default: m.PatientRegistration })));
const ProfileScreen = lazy(() => import('./components/profile/ProfileScreen').then(m => ({ default: m.ProfileScreen })));
const DoctorSchedule = lazy(() => import('./components/doctor/DoctorSchedule').then(m => ({ default: m.DoctorSchedule })));
const ClinicalNotes = lazy(() => import('./components/doctor/ClinicalNotes').then(m => ({ default: m.ClinicalNotes })));
const Prescriptions = lazy(() => import('./components/doctor/Prescriptions').then(m => ({ default: m.Prescriptions })));
const LabAiReviewPage = lazy(() => import('./pages/doctor/LabAiReviewPage'));
const ProcedureRoomPage = lazy(() => import('./pages/ProcedureRoomPage'));
const RevenueDashboard = lazy(() => import('./components/doctor/RevenueDashboard').then(m => ({ default: m.RevenueDashboard })));
const FeedbackDashboardPage = lazy(() => import('./components/doctor/FeedbackDashboardPage').then(m => ({ default: m.FeedbackDashboardPage })));
const TemplatesManagement = lazy(() => import('./components/prescriptions/TemplatesManagement').then(m => ({ default: m.TemplatesManagement })));
const PharmacyManagement = lazy(() => import('./components/pharmacy/PharmacyManagement').then(m => ({ default: m.PharmacyManagement })));
const PharmacyDashboard = lazy(() => import('./components/pharmacy/PharmacyDashboard').then(m => ({ default: m.PharmacyDashboard })));
const PharmacyMedicationInventory = lazy(() => import('./components/pharmacy/PharmacyMedicationInventory'));
const PharmacyApprovalManagement = lazy(() => import('./components/admin/PharmacyManagement'));
const QueueManagement = lazy(() => import('./components/staff/QueueManagement').then(m => ({ default: m.QueueManagement })));
const PatientRegistration = lazy(() => import('./components/staff/PatientRegistration').then(m => ({ default: m.PatientRegistration })));
const InventoryManagement = lazy(() => import('./components/admin/InventoryManagement').then(m => ({ default: m.InventoryManagement })));
const StaffManagement = lazy(() => import('./components/admin/StaffManagement').then(m => ({ default: m.StaffManagement })));
const AuditLogs = lazy(() => import('./components/admin/AuditLogs').then(m => ({ default: m.AuditLogs })));
const InpatientCensus = lazy(() => import('./components/inpatient/InpatientCensus').then(m => ({ default: m.InpatientCensus })));
const LabAssistantDashboard = lazy(() => import('./components/lab/LabTasksList').then(m => ({ default: m.LabAssistantDashboard })));
const LabReagentInventory = lazy(() => import('./components/lab/LabReagentInventory'));
const LabReceivingPage = lazy(() => import('./components/lab/LabReceivingPage'));
const LabQcPage = lazy(() => import('./components/lab/LabQcPage'));
const VerificationQueuePage = lazy(() => import('./components/lab/VerificationQueuePage'));
const CollectionQueuePage = lazy(() => import('./components/lab/CollectionQueuePage'));
const ResultEntryQueuePage = lazy(() => import('./components/lab/ResultEntryQueuePage'));
const ResultReviewQueuePage = lazy(() => import('./components/lab/ResultReviewQueuePage'));
const PatientVerificationPage = lazy(() => import('./components/lab/PatientVerificationPage'));
const SpecimenCollectionPage = lazy(() => import('./components/lab/SpecimenCollectionPage'));
const SpecimenTimelinePage = lazy(() => import('./components/lab/SpecimenTimelinePage'));
const ManualResultEntryPage = lazy(() => import('./components/lab/ManualResultEntryPage'));
const VerifyReleasePage = lazy(() => import('./components/lab/VerifyReleasePage'));
const ReferralDashboardPage = lazy(() => import('./pages/referrals/ReferralDashboardPage'));
const ReferralListPage = lazy(() => import('./pages/referrals/ReferralListPage').then(m => ({ default: m.ReferralListPage })));
const ReferralIncomingPage = lazy(() => import('./pages/referrals/ReferralListPage').then(m => ({ default: m.ReferralIncomingPage })));
const ReferralCreatePage = lazy(() => import('./pages/referrals/ReferralCreatePage'));
const ReferralDetailPage = lazy(() => import('./pages/referrals/ReferralDetailPage'));
const ExternalReferralPortalPage = lazy(() => import('./pages/referrals/ExternalReferralPortalPage'));
const ExternalDirectoryPage = lazy(() => import('./pages/referrals/ExternalDirectoryPage'));
const TransferDashboardPage = lazy(() => import('./pages/transfers/TransferDashboardPage'));
const TransferListPage = lazy(() => import('./pages/transfers/TransferListPage').then(m => ({ default: m.TransferListPage })));
const TransferIncomingPage = lazy(() => import('./pages/transfers/TransferListPage').then(m => ({ default: m.TransferIncomingPage })));
const TransferCreatePage = lazy(() => import('./pages/transfers/TransferCreatePage'));
const TransferDetailPage = lazy(() => import('./pages/transfers/TransferDetailPage'));
const PatientRegister = lazy(() => import('./components/patient-portal/PatientRegister').then(m => ({ default: m.PatientRegister })));
const PatientDashboard = lazy(() => import('./components/patient-portal/PatientDashboard').then(m => ({ default: m.PatientDashboard })));
const PatientFindCare = lazy(() => import('./components/patient-portal/PatientFindCare').then(m => ({ default: m.PatientFindCare })));
const PatientMessages = lazy(() => import('./components/patient-portal/PatientMessages').then(m => ({ default: m.PatientMessages })));
const PatientVitals = lazy(() => import('./components/patient-portal/PatientVitals').then(m => ({ default: m.PatientVitals })));
const PatientAppointments = lazy(() => import('./components/patient-portal/PatientAppointments').then(m => ({ default: m.PatientAppointments })));
const PatientReferralsTransfers = lazy(() => import('./components/patient-portal/PatientReferralsTransfers').then(m => ({ default: m.PatientReferralsTransfers })));
const PatientConsents = lazy(() => import('./components/patient-portal/PatientConsents'));
const PatientBills = lazy(() => import('./components/patient-portal/PatientBills').then(m => ({ default: m.PatientBills })));
const PatientPlans = lazy(() => import('./components/patient-portal/PatientPlans').then(m => ({ default: m.PatientPlans })));
const PatientPrescriptions = lazy(() => import('./components/patient-portal/PatientPrescriptions').then(m => ({ default: m.PatientPrescriptions })));
const PatientMedicalRecords = lazy(() => import('./components/patient-portal/PatientMedicalRecords').then(m => ({ default: m.PatientMedicalRecords })));
const PatientLabResults = lazy(() => import('./components/patient-portal/PatientLabResults').then(m => ({ default: m.PatientLabResults })));
const PatientProfile = lazy(() => import('./components/patient-portal/PatientProfile').then(m => ({ default: m.PatientProfile })));
const PatientFamily = lazy(() => import('./components/patient-portal/PatientFamily').then(m => ({ default: m.PatientFamily })));
const DoctorScheduleConfigurationPage = lazy(() => import('./components/appointments/DoctorScheduleConfigurationPage').then(m => ({ default: m.DoctorScheduleConfigurationPage })));
const AppointmentManagementComponent = lazy(() => import('./components/appointments/AppointmentManagementComponent').then(m => ({ default: m.AppointmentManagementComponent })));
const PatientAppointmentBookingComponent = lazy(() => import('./components/appointments/PatientAppointmentBookingComponent').then(m => ({ default: m.PatientAppointmentBookingComponent })));
const PharmacyRegistrationFlow = lazy(() => import('./pages/pharmacy/PharmacyRegistrationFlow'));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const MobileUploadPage = lazy(() => import('./pages/MobileUploadPage').then(m => ({ default: m.MobileUploadPage })));
const QRCheckinPage = lazy(() => import('./pages/QRCheckinPage').then(m => ({ default: m.QRCheckinPage })));
const PatientCheckinPage = lazy(() => import('./pages/PatientCheckinPage'));
const PatientFeedbackPage = lazy(() => import('./pages/PatientFeedbackPage'));
const SharedPrescriptionPage = lazy(() => import('./pages/SharedPrescriptionPage'));
const VerifyInvoicePage = lazy(() => import('./pages/VerifyInvoicePage'));
const NotificationInbox = lazy(() => import('./pages/NotificationInbox').then(m => ({ default: m.NotificationInbox })));
const ConsentApprovePage = lazy(() => import('./pages/ConsentApprovePage').then(m => ({ default: m.ConsentApprovePage })));
const FeeScheduleScreen = lazy(() => import('./pages/admin/FeeScheduleScreen'));
const ClinicNavSettingsPage = lazy(() => import('./pages/admin/ClinicNavSettingsPage'));
const ClinicAiSettingsPage  = lazy(() => import('./pages/admin/ClinicAiSettingsPage'));
const McpSettingsPage       = lazy(() => import('./pages/admin/McpSettingsPage'));
const ClinicKnowledgeBasePage = lazy(() => import('./pages/admin/ClinicKnowledgeBasePage'));
const ClinicEngagementSettingsPage = lazy(() => import('./pages/admin/ClinicEngagementSettingsPage'));
const ClinicCheckinSettingsPage = lazy(() => import('./pages/admin/ClinicCheckinSettingsPage'));
const GrowthDashboardPage = lazy(() => import('./pages/admin/GrowthDashboardPage'));
const CampaignsPage = lazy(() => import('./pages/admin/CampaignsPage'));
const FollowUpEnforcementPage = lazy(() => import('./pages/admin/FollowUpEnforcementPage'));
const HealthPackagesPage = lazy(() => import('./pages/admin/HealthPackagesPage'));
const ReferralsPage = lazy(() => import('./pages/admin/ReferralsPage'));
const RevenueIntelligencePage = lazy(() => import('./pages/admin/RevenueIntelligencePage'));
const FeedbackAnalyticsPage = lazy(() => import('./pages/admin/FeedbackAnalyticsPage'));
const QualityMeasuresPage = lazy(() => import('./pages/admin/QualityMeasuresPage'));
const ErasureRequestsPage = lazy(() => import('./pages/admin/ErasureRequestsPage'));
const TestDataStudioPage = lazy(() => import('./pages/admin/TestDataStudioPage'));
const CorporateProgramsPage = lazy(() => import('./pages/admin/CorporateProgramsPage'));
const ConsultationRecorderPage = lazy(() => import('./pages/ConsultationRecorderPage'));
const ClinicAccessPage = lazy(() => import('./pages/admin/ClinicAccessPage'));
const ConsentSettingsPage = lazy(() => import('./pages/admin/ConsentSettingsPage'));
const HospitalGroupsPage = lazy(() => import('./pages/admin/HospitalGroupsPage').then(m => ({ default: m.HospitalGroupsPage })));
const GroupDetailPage = lazy(() => import('./pages/admin/GroupDetailPage').then(m => ({ default: m.GroupDetailPage })));
const ClinicPlanPage = lazy(() => import('./pages/admin/ClinicPlanPage'));
const ClinicMedicationsList = lazy(() => import('./components/medications/ClinicMedicationsList').then(m => ({ default: m.default })));
const AddMedicationDialog = lazy(() => import('./components/medications/AddMedicationDialog').then(m => ({ default: m.default })));
const SuperAdminMedicationManagement = lazy(() => import('./components/admin/SuperAdminMedicationManagement').then(m => ({ default: m.default })));
const AdminAiSafetyReviewPage = lazy(() => import('./components/admin/ai-safety/AdminAiSafetyReviewPage'));

export const ColorModeContext = createContext({
  mode: 'light' as 'light' | 'dark',
  toggleColorMode: () => {},
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, clinic, loading, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user needs to change password and not already on that page, redirect
  if (userData?.initialLogin && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Super admins and specialized portal users don't need a clinic setup check
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Pharmacy, Lab, Group Admin, and Regional Admin users bypass clinic setup check
  if (userData && (
    userData.role === 'pharmacy_admin' ||
    userData.role === 'pharmacy_staff' ||
    userData.role === 'lab_assistant' ||
    userData.role === 'lab_admin' ||
    userData.role === 'lab_staff' ||
    userData.role === 'group_admin' ||
    userData.role === 'regional_admin'
  )) {
    return <>{children}</>;
  }

  // Clinic admins and staff need a completed clinic setup
  if (!userData || !clinic || !clinic.setupCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  // Only super_admin role can access these routes
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function ClinicAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  // Super admins should use their own dashboard
  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

function ClinicAdminOrSuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  if (userData.role !== 'clinic_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function ClinicAdminOrDoctorRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (!['clinic_admin', 'doctor'].includes(userData.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function RoleRoute({ children, roles }: { children: React.ReactNode; roles: UserRole[] }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  if (!roles.includes(userData.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Route → module/permission requirements now live in a shared module so the sidebar and dashboard
// buttons can hide options the user can't access (see lib/routePermissions.ts).

/**
 * Sits inside BrowserRouter and intercepts every navigation.
 * Redirects to /dashboard when a module is disabled or the user lacks the permission.
 * super_admin always passes through.
 */
function DynamicPermissionGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  const { can, moduleEnabled, isLoading: permLoading } = usePermissions();

  // Not authenticated or super_admin — let existing route guards handle it
  if (!user || isSuperAdmin || authLoading) return <>{children}</>;

  const req = getRouteRequirement(location.pathname);
  if (!req) return <>{children}</>;

  // Show spinner while auth is resolving or permissions are still loading for a guarded route
  if (permLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (req.module && !moduleEnabled(req.module)) {
    toast.error('This feature is not enabled for your clinic.');
    return <Navigate to="/dashboard" replace state={{ from: location, reason: 'module_disabled' }} />;
  }
  if (req.permission && !can(req.permission)) {
    toast.error("You don't have permission to access this page.");
    return <Navigate to="/dashboard" replace state={{ from: location, reason: 'no_permission' }} />;
  }

  return <>{children}</>;
}

function RoleDashboard() {
  const { userData, loading, isSuperAdmin } = useAuth();

  const contentFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Route based on user role
  if (userData?.role === 'doctor') {
    return (
      <Layout>
        <Suspense fallback={contentFallback}><DoctorDashboard /></Suspense>
      </Layout>
    );
  }

  if (userData?.role === 'staff') {
    return <Navigate to="/staff/queue" replace />;
  }

  if (userData?.role === 'clinic_admin') {
    return (
      <Layout>
        <Suspense fallback={contentFallback}><ClinicDashboard /></Suspense>
      </Layout>
    );
  }

  if (userData?.role === 'pharmacy_admin' || userData?.role === 'pharmacy_staff') {
    return (
      <Layout>
        <Suspense fallback={contentFallback}><PharmacyDashboard /></Suspense>
      </Layout>
    );
  }

  if (userData?.role === 'lab_assistant' || userData?.role === 'lab_admin' || userData?.role === 'lab_staff') {
    return (
      <Layout>
        <Suspense fallback={contentFallback}><LabAssistantDashboard /></Suspense>
      </Layout>
    );
  }

  if (userData?.role === 'group_admin' || userData?.role === 'regional_admin') {
    const destination = userData.hospitalGroupId
      ? `/admin/hospital-groups/${userData.hospitalGroupId}`
      : '/admin/hospital-groups';
    return <Navigate to={destination} replace />;
  }

  return <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isSuperAdmin } = useAuth();

  console.log('🚪 PublicRoute - loading:', loading, 'user:', user?.email, 'userData:', !!userData);

  if (loading) {
    console.log('🚪 PublicRoute - showing loading spinner');
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (user && userData) {
    console.log('🚪 PublicRoute - user authenticated, redirecting');
    // Super admins go to their dashboard
    if (isSuperAdmin) {
      return <Navigate to="/admin" replace />;
    }
    // Everyone else goes to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  console.log('🚪 PublicRoute - rendering children');
  return <>{children}</>;
}

function PatientPublicRoute({ children }: { children: React.ReactNode }) {
  const { patient, loading } = usePatientAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (patient) {
    return <Navigate to="/patient/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Shared layout wrapper for all authenticated, clinic-aware routes.
 * By nesting routes under this component, the Layout (sidebar + header) is mounted
 * ONCE and persists across navigations — only the <Outlet> content changes.
 */
function PrivateLayoutRoute() {
  const { user, userData, clinic, loading, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (userData?.initialLogin && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Super admins have no clinic — bypass clinic-setup check but still get Layout
  if (isSuperAdmin) {
    return <Layout><Outlet /></Layout>;
  }

  // Pharmacy, lab, group admin, and regional admin roles bypass clinic-setup check
  if (userData && ['pharmacy_admin', 'pharmacy_staff', 'lab_assistant', 'lab_admin', 'lab_staff', 'group_admin', 'regional_admin'].includes(userData.role)) {
    return <Layout><Outlet /></Layout>;
  }

  // Everyone else must have a completed clinic setup
  if (!userData || !clinic || !clinic.setupCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout><Outlet /></Layout>;
}

/**
 * Inline role guard used inside PrivateLayoutRoute child routes.
 * Auth/loading is already handled by the parent — this just redirects on role mismatch.
 */
function RequireRole({ children, roles }: { children: React.ReactNode; roles: UserRole[] }) {
  const { userData, isSuperAdmin } = useAuth();
  // Super admin can always access any role-protected route
  if (isSuperAdmin) return <>{children}</>;
  if (!userData || !roles.includes(userData.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Clinic-admin only — super_admin explicitly excluded (no clinic context). */
function RequireClinicAdmin({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth();
  if (!userData || userData.role !== 'clinic_admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Helper component to extract URL params for appointment booking
const AppointmentBookingWrapper = () => {
  const { doctorId, doctorName } = useParams<{ doctorId: string; doctorName: string }>();

  if (!doctorId || !doctorName) {
    return <Navigate to="/patient/dashboard" />;
  }

  return (
    <PatientAppointmentBookingComponent
      doctorId={doctorId}
      doctorName={decodeURIComponent(doctorName)}
    />
  );
};

// Helper component for doctor schedule configuration with auth
const DoctorScheduleConfigWrapper = () => {
  const { userData } = useAuth();

  if (!userData?.id) {
    return <Navigate to="/login" />;
  }

  return <DoctorScheduleConfigurationPage doctorId={userData.id} />;
};

// Helper component for appointment management with auth
const AppointmentManagementWrapper = () => {
  const { userData, clinic } = useAuth();

  if (!userData?.id || !clinic?.id) {
    return <Navigate to="/login" />;
  }

  return (
    <AppointmentManagementComponent
      doctorId={userData.id}
      clinicId={clinic.id}
    />
  );
};

function AppRoutes() {
  // Content-area spinner — sidebar stays visible while a lazy chunk loads
  const contentFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Box>
  );
  return (
    <DynamicPermissionGuard>
      <ErrorBoundary>
        <Routes>
          {/* ── Public / unauthenticated routes ─────────────────────────── */}
          <Route path="/login" element={<PublicRoute><Suspense fallback={null}><Login /></Suspense></PublicRoute>} />
          <Route path="/pharmacy/login" element={<PublicRoute><Suspense fallback={null}><PharmacyLogin /></Suspense></PublicRoute>} />
          <Route path="/patient/login" element={<PatientPublicRoute><Suspense fallback={null}><PatientLogin /></Suspense></PatientPublicRoute>} />
          <Route path="/patient/register" element={<PatientPublicRoute><Suspense fallback={null}><PatientRegister /></Suspense></PatientPublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><Suspense fallback={null}><ForgotPassword /></Suspense></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><Suspense fallback={null}><ResetPassword /></Suspense></PublicRoute>} />
          <Route path="/activate-account" element={<PublicRoute><Suspense fallback={null}><ResetPassword /></Suspense></PublicRoute>} />
          <Route path="/change-password" element={<PrivateRoute><Suspense fallback={null}><ForcedPasswordReset /></Suspense></PrivateRoute>} />
          <Route path="/mobile-upload" element={<Suspense fallback={null}><MobileUploadPage /></Suspense>} />
          <Route path="/checkin/:clinicId" element={<Suspense fallback={null}><QRCheckinPage /></Suspense>} />
          <Route path="/checkin/apt/:checkinToken" element={<Suspense fallback={null}><PatientCheckinPage /></Suspense>} />
          <Route path="/feedback/apt/:feedbackToken" element={<Suspense fallback={null}><PatientFeedbackPage /></Suspense>} />
          <Route path="/rx/:token" element={<Suspense fallback={null}><SharedPrescriptionPage /></Suspense>} />
          <Route path="/verify-invoice" element={<Suspense fallback={null}><VerifyInvoicePage /></Suspense>} />
          <Route path="/register/pharmacy" element={<Suspense fallback={null}><PharmacyRegistrationFlow /></Suspense>} />
          <Route path="/accept-invitation" element={<PublicRoute><Suspense fallback={null}><AcceptInvitation /></Suspense></PublicRoute>} />
          <Route path="/terms" element={<Suspense fallback={null}><TermsPage /></Suspense>} />
          <Route path="/consent/approve" element={<Suspense fallback={null}><ConsentApprovePage /></Suspense>} />
          <Route path="/external-referral/:token" element={<Suspense fallback={null}><ExternalReferralPortalPage /></Suspense>} />

          {/* ── Dashboard (handles its own Layout per role) ──────────────── */}
          <Route path="/dashboard" element={<PrivateRoute><RoleDashboard /></PrivateRoute>} />
          <Route path="/setup" element={<ClinicAdminRoute><Suspense fallback={null}><ClinicSetup /></Suspense></ClinicAdminRoute>} />

          {/* ── Patient portal — shared layout persists across navigation (was remounted per-route) ── */}
          <Route element={<PatientPortalLayout />}>
            <Route path="/patient/dashboard" element={<Suspense fallback={contentFallback}><PatientDashboard /></Suspense>} />
            <Route path="/patient/find-care" element={<Suspense fallback={contentFallback}><PatientFindCare /></Suspense>} />
            <Route path="/patient/vitals" element={<Suspense fallback={contentFallback}><PatientVitals /></Suspense>} />
            <Route path="/patient/appointments" element={<Suspense fallback={contentFallback}><PatientAppointments /></Suspense>} />
            <Route path="/patient/messages" element={<Suspense fallback={contentFallback}><PatientMessages /></Suspense>} />
            <Route path="/patient/bills" element={<Suspense fallback={contentFallback}><PatientBills /></Suspense>} />
            <Route path="/patient/plans" element={<Suspense fallback={contentFallback}><PatientPlans /></Suspense>} />
            <Route path="/patient/prescriptions" element={<Suspense fallback={contentFallback}><PatientPrescriptions /></Suspense>} />
            <Route path="/patient/records" element={<Suspense fallback={contentFallback}><PatientMedicalRecords /></Suspense>} />
            <Route path="/patient/referrals" element={<Suspense fallback={contentFallback}><PatientReferralsTransfers /></Suspense>} />
            {/* Patient-controlled cross-clinic record sharing — approve, deny, revoke */}
            <Route path="/patient/consents" element={<Suspense fallback={contentFallback}><PatientConsents /></Suspense>} />
            <Route path="/patient/lab-results" element={<Suspense fallback={contentFallback}><PatientLabResults /></Suspense>} />
            <Route path="/patient/profile" element={<Suspense fallback={contentFallback}><PatientProfile /></Suspense>} />
            <Route path="/patient/family" element={<Suspense fallback={contentFallback}><PatientFamily /></Suspense>} />
          </Route>

          {/* ── App routes — shared Layout, sidebar persists on navigation ── */}
          <Route element={<PrivateLayoutRoute />}>
            {/* OPD Core */}
            <Route path="/tokens"          element={<Suspense fallback={contentFallback}><TokenList /></Suspense>} />
            <Route path="/patients"        element={<Suspense fallback={contentFallback}><PatientSearch /></Suspense>} />
            <Route path="/patient-details" element={<Suspense fallback={contentFallback}><PatientDetailsPage /></Suspense>} />
            <Route path="/patients/register" element={<Suspense fallback={contentFallback}><PatientRegistrationForm /></Suspense>} />
            <Route path="/inpatients"      element={<Suspense fallback={contentFallback}><InpatientCensus /></Suspense>} />
            {/* clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via /admin/clinic-access's Nav Visibility tab */}
            <Route path="/referrals"          element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><ReferralDashboardPage /></RequireRole></Suspense>} />
            <Route path="/referrals/list"     element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><ReferralListPage /></RequireRole></Suspense>} />
            <Route path="/referrals/incoming" element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><ReferralIncomingPage /></RequireRole></Suspense>} />
            <Route path="/referrals/create"   element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff']}><ReferralCreatePage /></RequireRole></Suspense>} />
            <Route path="/referrals/directory" element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><ExternalDirectoryPage /></RequireRole></Suspense>} />
            <Route path="/referrals/:id"      element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><ReferralDetailPage /></RequireRole></Suspense>} />
            <Route path="/transfers"          element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><TransferDashboardPage /></RequireRole></Suspense>} />
            <Route path="/transfers/list"     element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><TransferListPage /></RequireRole></Suspense>} />
            <Route path="/transfers/incoming" element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'group_admin']}><TransferIncomingPage /></RequireRole></Suspense>} />
            <Route path="/transfers/create"   element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff']}><TransferCreatePage /></RequireRole></Suspense>} />
            <Route path="/transfers/:id"      element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'clinic_admin', 'staff', 'group_admin']}><TransferDetailPage /></RequireRole></Suspense>} />
            <Route path="/profile"         element={<Suspense fallback={contentFallback}><ProfileScreen /></Suspense>} />
            <Route path="/notifications"   element={<Suspense fallback={contentFallback}><NotificationInbox /></Suspense>} />
            <Route path="/communications"  element={<Suspense fallback={contentFallback}><NotificationInbox /></Suspense>} />
            <Route path="/appointments/book/:doctorId/:doctorName" element={<Suspense fallback={contentFallback}><AppointmentBookingWrapper /></Suspense>} />

            {/* Billing */}
            <Route path="/billing"       element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'staff', 'doctor', 'group_admin']}><BillingForm /></RequireRole></Suspense>} />
            <Route path="/billing/queue" element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'staff', 'group_admin']}><BillingQueueScreen /></RequireRole></Suspense>} />
            <Route path="/medications"   element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'staff', 'doctor']}><ClinicMedicationsList /></RequireRole></Suspense>} />
            <Route path="/admin/medications"   element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><SuperAdminMedicationManagement /></RequireRole></Suspense>} />
            <Route path="/admin/ai-safety-review" element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><AdminAiSafetyReviewPage /></RequireRole></Suspense>} />

            {/* Follow-ups */}
            <Route path="/follow-ups" element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'staff', 'doctor', 'group_admin']}><FollowUpQueuePage /></RequireRole></Suspense>} />

            {/* Reports */}
            <Route path="/reports"               element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'clinic_admin', 'group_admin']}><DailyReport /></RequireRole></Suspense>} />
            <Route path="/reports/vitals-export" element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'clinic_admin', 'group_admin']}><VitalsExport /></RequireRole></Suspense>} />

            {/* Doctor */}
            <Route path="/doctor/schedule"        element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'clinic_admin']}><DoctorSchedule /></RequireRole></Suspense>} />
            <Route path="/doctor/notes"           element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><ClinicalNotes /></RequireRole></Suspense>} />
            <Route path="/doctor/prescriptions"   element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><Prescriptions /></RequireRole></Suspense>} />
            {/* Doctor's AI-assisted lab review worklist. Keep in sync with lib/routePermissions.ts */}
            <Route path="/doctor/lab-review"      element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><LabAiReviewPage /></RequireRole></Suspense>} />
            {/* Procedure room worklist (clinical_procedures). Nurses run many procedures, so STAFF is
                included — matches ClinicalProcedureController and lib/routePermissions.ts. */}
            <Route path="/procedures"            element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor', 'staff', 'clinic_admin']}><ProcedureRoomPage /></RequireRole></Suspense>} />
            <Route path="/doctor/revenue"         element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><RevenueDashboard /></RequireRole></Suspense>} />
            <Route path="/doctor/feedback"        element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><FeedbackDashboardPage /></RequireRole></Suspense>} />
            <Route path="/doctor/templates"       element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><TemplatesManagement /></RequireRole></Suspense>} />
            <Route path="/templates"              element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><TemplatesManagement /></RequireRole></Suspense>} />
            {/* clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via /admin/clinic-access's Nav Visibility tab */}
            <Route path="/doctor/schedule-config" element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><DoctorScheduleConfigWrapper /></RequireRole></Suspense>} />
            <Route path="/doctor/appointments"    element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><AppointmentManagementWrapper /></RequireRole></Suspense>} />

            {/* Staff */}
            <Route path="/staff/queue"    element={<Suspense fallback={contentFallback}><RequireRole roles={['staff']}><QueueManagement /></RequireRole></Suspense>} />
            <Route path="/staff/register" element={<Suspense fallback={contentFallback}><RequireRole roles={['staff', 'clinic_admin']}><PatientRegistration /></RequireRole></Suspense>} />

            {/* Lab */}
            <Route path="/lab/tasks" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><LabAssistantDashboard /></RequireRole></Suspense>} />
            <Route path="/lab/inventory" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><LabReagentInventory /></RequireRole></Suspense>} />
            <Route path="/lab/receiving" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><LabReceivingPage /></RequireRole></Suspense>} />
            <Route path="/lab/qc" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><LabQcPage /></RequireRole></Suspense>} />
            <Route path="/lab/verification" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><VerificationQueuePage /></RequireRole></Suspense>} />
            <Route path="/lab/collection" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><CollectionQueuePage /></RequireRole></Suspense>} />
            <Route path="/lab/result-entry" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><ResultEntryQueuePage /></RequireRole></Suspense>} />
            <Route path="/lab/result-review" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><ResultReviewQueuePage /></RequireRole></Suspense>} />
            <Route path="/lab/accessions/:accessionId/verify" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><PatientVerificationPage /></RequireRole></Suspense>} />
            <Route path="/lab/accessions/:accessionId/collect" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><SpecimenCollectionPage /></RequireRole></Suspense>} />
            <Route path="/lab/accessions/:accessionId/timeline" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin', 'doctor']}><SpecimenTimelinePage /></RequireRole></Suspense>} />
            <Route path="/lab/accessions/:accessionId/specimens/:specimenId/results" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><ManualResultEntryPage /></RequireRole></Suspense>} />
            <Route path="/lab/accessions/:accessionId/specimens/:specimenId/release" element={<Suspense fallback={contentFallback}><RequireRole roles={['lab_admin', 'lab_staff', 'clinic_admin']}><VerifyReleasePage /></RequireRole></Suspense>} />

            {/* Pharmacy */}
            {/* Medication Stock — reachable by clinic staff who manage in-house pharmacy stock,
                not just dedicated pharmacy accounts. Nav visibility is still governed by the
                PHARMACY module + PHARMACY_ITEMS_VIEW permission; this list is defense-in-depth. */}
            <Route path="/pharmacy/inventory"            element={<Suspense fallback={contentFallback}><RequireRole roles={['pharmacy_admin', 'pharmacy_staff', 'clinic_admin', 'doctor', 'group_admin']}><PharmacyMedicationInventory /></RequireRole></Suspense>} />
            <Route path="/pharmacy/medication-inventory" element={<Suspense fallback={contentFallback}><PharmacyMedicationInventory /></Suspense>} />

            {/* Clinic admin */}
            <Route path="/admin/inventory"         element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'group_admin']}><InventoryManagement /></RequireRole></Suspense>} />
            <Route path="/admin/staff"             element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'group_admin']}><StaffManagement /></RequireRole></Suspense>} />
            <Route path="/admin/fee-schedule"      element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'group_admin']}><FeeScheduleScreen /></RequireRole></Suspense>} />
            {/* clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via /admin/clinic-access's Nav Visibility tab */}
            <Route path="/admin/nav-settings"      element={<Suspense fallback={contentFallback}><RequireRole roles={['group_admin']}><ClinicNavSettingsPage /></RequireRole></Suspense>} />
            <Route path="/admin/ai-settings"       element={<Suspense fallback={contentFallback}><RequireClinicAdmin><ClinicAiSettingsPage /></RequireClinicAdmin></Suspense>} />
            <Route path="/admin/mcp-settings"      element={<Suspense fallback={contentFallback}><RequireClinicAdmin><McpSettingsPage /></RequireClinicAdmin></Suspense>} />
            <Route path="/admin/clinic-knowledge-base" element={<Suspense fallback={contentFallback}><RequireClinicAdmin><ClinicKnowledgeBasePage /></RequireClinicAdmin></Suspense>} />
            <Route path="/admin/engagement"        element={<Suspense fallback={contentFallback}><RequireClinicAdmin><ClinicEngagementSettingsPage /></RequireClinicAdmin></Suspense>} />
            <Route path="/admin/checkin-settings"  element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'super_admin']}><ClinicCheckinSettingsPage /></RequireRole></Suspense>} />
            <Route path="/growth"                  element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'doctor']}><GrowthDashboardPage /></RequireRole></Suspense>} />
            <Route path="/growth/campaigns"        element={<Suspense fallback={contentFallback}><RequireClinicAdmin><CampaignsPage /></RequireClinicAdmin></Suspense>} />
            <Route path="/consultation"            element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><ConsultationRecorderPage /></RequireRole></Suspense>} />
            <Route path="/admin/in-house-pharmacies" element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><InHousePharmacyManagement /></RequireRole></Suspense>} />
            <Route path="/admin/labs"               element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><LabManagement /></RequireRole></Suspense>} />
            <Route path="/admin/audit"             element={<Suspense fallback={contentFallback}><AuditLogs /></Suspense>} />
            <Route path="/admin/follow-ups"         element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'staff']}><FollowUpEnforcementPage /></RequireRole></Suspense>} />
            <Route path="/admin/health-packages"    element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><HealthPackagesPage /></RequireRole></Suspense>} />
            {/* clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via /admin/clinic-access's Nav Visibility tab */}
            <Route path="/admin/referrals"          element={<Suspense fallback={contentFallback}><RequireRole roles={['staff']}><ReferralsPage /></RequireRole></Suspense>} />
            <Route path="/admin/revenue-intelligence" element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><RevenueIntelligencePage /></RequireRole></Suspense>} />
            <Route path="/admin/feedback-nps"         element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><FeedbackAnalyticsPage /></RequireRole></Suspense>} />
            {/* clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via /admin/clinic-access's Nav Visibility tab */}
            <Route path="/admin/quality-measures"     element={<Suspense fallback={contentFallback}><RequireRole roles={['doctor']}><QualityMeasuresPage /></RequireRole></Suspense>} />
            <Route path="/admin/erasure-requests"     element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><ErasureRequestsPage /></RequireRole></Suspense>} />
            <Route path="/admin/corporate"          element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin']}><CorporateProgramsPage /></RequireRole></Suspense>} />
            <Route path="/settings/doctors"        element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'group_admin']}><DoctorsSettings /></RequireRole></Suspense>} />
            <Route path="/settings/departments"    element={<Suspense fallback={contentFallback}><RequireRole roles={['clinic_admin', 'group_admin']}><DepartmentsSettings /></RequireRole></Suspense>} />

            {/* Super admin */}
            <Route path="/admin"                       element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><SuperAdminDashboard /></RequireRole></Suspense>} />
            <Route path="/admin/lab-metrics"           element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><LabMetricManagement /></RequireRole></Suspense>} />
            <Route path="/admin/clinic-access"         element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><ClinicAccessPage /></RequireRole></Suspense>} />
            {/* Platform-wide cross-clinic consent policy — transports, validity, enforcement cutover */}
            <Route path="/admin/consent-settings"      element={<Suspense fallback={contentFallback}><RequireRole roles={["super_admin"]}><ConsentSettingsPage /></RequireRole></Suspense>} />
            <Route path="/admin/error-logs"            element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><SystemErrorLogs /></RequireRole></Suspense>} />
            <Route path="/admin/pharmacies"            element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><PharmacyApprovalManagement /></RequireRole></Suspense>} />
            <Route path="/admin/pharmacy-approvals"    element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><PharmacyApprovalManagement /></RequireRole></Suspense>} />
            <Route path="/admin/hospital-groups"       element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin', 'group_admin', 'regional_admin']}><HospitalGroupsPage /></RequireRole></Suspense>} />
            <Route path="/admin/hospital-groups/:groupId" element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin', 'group_admin', 'regional_admin']}><GroupDetailPage /></RequireRole></Suspense>} />
            <Route path="/admin/clinics/:clinicId/plan" element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><ClinicPlanPage /></RequireRole></Suspense>} />
            <Route path="/admin/test-data"              element={<Suspense fallback={contentFallback}><RequireRole roles={['super_admin']}><TestDataStudioPage /></RequireRole></Suspense>} />
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          {/* Catch-all: any unmatched path lands on the role dashboard instead of a blank screen. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </DynamicPermissionGuard>
  );
}

function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('color-mode');
    return stored === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('color-mode', mode);
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    }),
    [mode]
  );

  const theme = useMemo(
    () => createClinicTheme(mode),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <TourProvider>
          <AuthProvider>
            <TenantProvider>
            <PermissionProvider>
            <WebSocketProvider>
              <PatientAuthProvider>
                <PatientTenantProvider>
                <PatientDependentProvider>
                <PatientDetailsProvider>
                  <Toaster position="bottom-right" />
                  {/* CHATBOT - Commented out as not using chatbot feature */}
                  {/* <ChatProvider> */}
                    <SessionTimeoutWarning />
                    <BrowserRouter>
                      <AppLockProvider>
                        <LockScreen />
                        <ErrorBoundary>
                          <AppRoutes />
                        </ErrorBoundary>
                      </AppLockProvider>
                    </BrowserRouter>
                  {/* </ChatProvider> */}
                </PatientDetailsProvider>
                </PatientDependentProvider>
                </PatientTenantProvider>
              </PatientAuthProvider>
            </WebSocketProvider>
            </PermissionProvider>
            </TenantProvider>
          </AuthProvider>
        </TourProvider>
      </AppThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;